import { useMemo, useSyncExternalStore } from 'react';
import type { Dict } from './dict/types';

/**
 * Sprachen (docs/18): Deutsch ist die Quellsprache — jeder Text steht im Code
 * auf Deutsch und ist zugleich der Schlüssel. `t('Anmelden')` liefert die
 * Übersetzung für die gewählte Sprache oder den deutschen Text, wenn es noch
 * keine gibt. So lässt sich die App Seite für Seite übersetzen, ohne dass
 * irgendwo ein leerer Schlüssel erscheint. Platzhalter: {n}, {name} …
 *
 * Auswahl: im Menü „Sprache & Darstellung" (components/Darstellung.tsx),
 * gemerkt im Browser; beim ersten Besuch nach der Browsersprache. Datum und
 * Uhrzeit folgen der Sprache.
 */
export type Lang = 'de' | 'en' | 'it';
export const LANGS: { key: Lang; label: string; name: string }[] = [
  { key: 'de', label: 'DE', name: 'Deutsch' },
  { key: 'en', label: 'EN', name: 'English' },
  { key: 'it', label: 'IT', name: 'Italiano' },
];
const KEY = 'airmail.lang';
const LOCALE: Record<Lang, string> = { de: 'de-DE', en: 'en-GB', it: 'it-IT' };

function istLang(s: string | null): s is Lang {
  return s === 'de' || s === 'en' || s === 'it';
}

/**
 * `?lang=it` in der Adresse — vor oder hinter dem `#` (`/?lang=it`,
 * `/#/join?t=…&lang=it`). Für Links von außen: Ein italienischer Verein soll
 * die App gleich auf Italienisch bekommen, statt erst umzustellen. Der Wert
 * wird gemerkt, wie ein Klick auf den Umschalter.
 */
function ausAdresse(): Lang | null {
  try {
    const { search, hash } = window.location;
    const hinterHash = hash.includes('?') ? hash.slice(hash.indexOf('?')) : '';
    for (const q of [search, hinterHash]) {
      const l = new URLSearchParams(q).get('lang');
      if (istLang(l)) return l;
    }
  } catch {
    /* egal */
  }
  return null;
}

function detect(): Lang {
  const gewuenscht = ausAdresse();
  if (gewuenscht) {
    try {
      localStorage.setItem(KEY, gewuenscht);
    } catch {
      /* egal */
    }
    return gewuenscht;
  }
  try {
    const saved = localStorage.getItem(KEY);
    if (istLang(saved)) return saved;
  } catch {
    /* egal */
  }
  const nav = (navigator.language ?? 'de').toLowerCase();
  if (nav.startsWith('it')) return 'it';
  if (nav.startsWith('en')) return 'en';
  return 'de';
}

let current: Lang = detect();
const listeners = new Set<() => void>();
/**
 * Zählt jede Änderung, die neu rendern muss: Sprachwechsel und das Eintreffen
 * des Wörterbuchs. `useLang` hängt daran — sonst bliebe die Seite nach dem
 * Nachladen deutsch, weil sich die Sprache selbst ja nicht geändert hat.
 */
let stand = 0;
function melde(): void {
  stand++;
  for (const fn of listeners) fn();
}

/**
 * Wörterbuch EN/IT — **erst bei Bedarf geladen**. Deutsch ist die Quellsprache
 * und braucht keins; vorher lud jeder deutsche Besucher alle Übersetzungen
 * mit (rund 70 KB gzip). Bis es da ist, erscheint der deutsche Text; danach
 * rendert alles neu (`melde`). `t()` bleibt dabei synchron.
 */
let DICT: Dict = {};
let ENGINE: Dict = {};
let laden: Promise<void> | null = null;
export function woerterbuch(): Promise<void> {
  laden ??= Promise.all([import('./dict'), import('./dict/engine')]).then(
    ([d, e]) => {
      DICT = d.DICT;
      ENGINE = e.engine;
      melde();
    },
    (err: unknown) => {
      // Offline ohne zwischengespeicherten Baustein: beim nächsten Sprachwechsel noch einmal versuchen
      laden = null;
      throw err;
    },
  );
  return laden;
}
/** Nachschlagen für `tm()` (i18n/engine.ts) — leer, solange das Wörterbuch lädt */
export function woerterbuchEintrag(de: string): Dict[string] | undefined {
  return DICT[de];
}
export function engineEintrag(de: string): Dict[string] | undefined {
  return ENGINE[de];
}
function brauchtWoerterbuch(): void {
  if (current !== 'de') woerterbuch().catch(() => undefined);
}

export function getLang(): Lang {
  return current;
}
export function setLang(l: Lang): void {
  current = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* egal */
  }
  document.documentElement.lang = l;
  brauchtWoerterbuch();
  melde();
}
document.documentElement.lang = current;
brauchtWoerterbuch();

function useStand(): number {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => stand,
  );
}
export function useLang(): Lang {
  useStand();
  return current;
}
/** BCP-47-Locale für Datum/Uhrzeit */
export function locale(): string {
  return LOCALE[current];
}

function fill(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}
/** Übersetzung für die aktuelle Sprache; Deutsch, wenn keine da ist */
export function t(de: string, vars?: Record<string, string | number>): string {
  const e = DICT[de];
  // Gleiches deutsches Wort, anderer Sinn: 'offen|Startgeld' vs. 'offen' — der Teil nach '|' ist nur Kontext
  const bar = de.indexOf('|');
  const plain = bar >= 0 ? de.slice(0, bar) : de;
  return fill(current === 'de' || !e ? plain : e[current], vars);
}
/**
 * Für Komponenten: rendert neu, wenn die Sprache wechselt.
 *
 * Zurück kommt bewusst **nicht** das `t` von oben, sondern je Sprache (und
 * je Stand des nachgeladenen Wörterbuchs) ein
 * eigenes: Wer `useMemo(…, [t])` schreibt — und das tun mehrere Seiten, weil
 * sie ganze Zeilen vorberechnen —, bekäme sonst beim Sprachwechsel den alten
 * Stand zurück. Die Komponente rendert zwar neu, der Memo-Eintrag bleibt aber
 * gültig, weil `t` immer dieselbe Funktion war. Genau so blieb in der
 * Zuschauer-Ansicht „spielt gerade" deutsch, bis man die Seite neu lud
 * (Daniel, 09/2026). Mit einer neuen Funktion je Sprache rechnet jeder solche
 * Memo von selbst neu.
 */
export function useT(): typeof t {
  // Neue Funktion je Sprache *und* nach dem Nachladen des Wörterbuchs
  const n = useStand();
  return useMemo(() => {
    // `n` ist der Grund für den Memo: eine neue Funktion je Stand
    void n;
    return ((de: string, vars?: Record<string, string | number>) => t(de, vars)) as typeof t;
  }, [n]);
}
